import { defineCatalog } from '@json-render/core';
import { schema } from '@json-render/react/schema';
import { z } from 'zod';

const badgeVariant = z.enum(['accent', 'error', 'info', 'neutral', 'success', 'warning']);
const badgeShape = z.object({ label: z.string(), variant: badgeVariant.optional() });
const priority = z.enum(['critical', 'high', 'medium', 'low']);

export const catalog = defineCatalog(schema, {
  components: {
    Markdown: {
      props: z.object({
        content: z.string(),
      }),
      description:
        'Renders markdown content. Use ONLY for narrative prose — executive summaries, analysis paragraphs. Keep it concise: 2-3 sentences for summaries, never walls of text. Do NOT repeat data shown in StatRow, KVList, or other structured components. NEVER use as root element. NEVER use Markdown tables — use DataTable instead. Markdown blocks are short prose: do not include headings or `---` dividers.',
    },

    KVList: {
      props: z.object({
        title: z.string().optional(),
        items: z.array(
          z.object({
            key: z.string(),
            value: z.string(),
            badge: badgeShape.optional(),
          }),
        ),
      }),
      description:
        "Key-value list for structured facts. Keep keys short. Each item must have a non-empty value or a badge — do not emit empty placeholder rows. Never use for methodology metadata (source tiers, vendor checklists, categories checked); fold those into the corresponding finding's description or evidence. Do NOT duplicate data that already appears in StatRow or other components.",
    },

    StatRow: {
      props: z.object({
        title: z.string().optional(),
        stats: z.array(
          z.object({
            label: z.string(),
            value: z.string(),
            badge: badgeShape.optional(),
            description: z.string().optional(),
          }),
        ),
      }),
      description:
        'Horizontal row of metric cards (up to 4). Only for real data — never invent values. Once a number appears here, do NOT repeat it in Markdown or other components.',
    },

    DataTable: {
      props: z.object({
        title: z.string().optional(),
        columns: z.array(z.object({ key: z.string(), label: z.string() })),
        rows: z.array(z.record(z.string(), z.string())),
      }),
      description:
        'Tabular data with header row. Use for transaction lists, document inventories, multi-column data. NEVER for screening/beneficial ownership (use ScreeningResults). Keep cell values concise.',
    },

    Timeline: {
      props: z.object({
        title: z.string().optional(),
        events: z.array(
          z.object({
            id: z.string(),
            timestamp: z.string(),
            title: z.string(),
            description: z.string().optional(),
            type: z.enum(['default', 'warning', 'error', 'success']).optional(),
          }),
        ),
      }),
      description:
        'Chronological timeline. Titles: short and factual (e.g. "Risk rating upgraded to high"). Descriptions: one sentence of context, optional. id = short slug (e.g. "inc-2018").',
    },

    TagList: {
      props: z.object({
        title: z.string().optional(),
        tags: z.array(
          z.object({
            label: z.string(),
            variant: badgeVariant.optional(),
          }),
        ),
      }),
      description:
        'A collection of labeled badge tags. Use for risk flags, categories, watchlist matches, or any set of categorical labels.',
    },

    NetworkGraph: {
      props: z.object({
        title: z.string().optional(),
        nodes: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
            sublabel: z.string().optional(),
            type: z.enum(['subject', 'person', 'business', 'account', 'transaction', 'document']),
          }),
        ),
        edges: z.array(
          z.object({
            from: z.string(),
            to: z.string(),
            type: z.enum(['owns', 'controls', 'transacts', 'linked_to', 'documents', 'unknown']),
            label: z.string().optional(),
          }),
        ),
        height: z.number().optional(),
      }),
      description:
        'Force-directed network graph for ownership structures, fund flow, relationships. Node types: subject, person, business, account, transaction, document. Edge types: owns (label for %), controls, transacts, linked_to, documents, unknown. Keep labels short. Do NOT add TagList or legends as children.',
    },

    ScreeningResults: {
      props: z.object({
        title: z.string().optional(),
        subjects: z.array(
          z.object({
            name: z.string(),
            kind: z.enum(['person', 'business']).optional(),
            role: z.string().optional(),
            metadata: z.string().optional(),
            checks: z.array(
              z.object({
                type: z.enum(['pep', 'sanctions', 'adverse_media', 'watchlist']),
                status: z.enum(['clear', 'hit', 'unknown']),
                detail: z.string().optional(),
                source: z.string().optional(),
              }),
            ),
          }),
        ),
      }),
      description:
        'Screening results per person/entity — one card each. metadata: dot-separated summary (e.g. "53% owner · Bolivian national"). detail: brief hit description, only if real evidence exists — never fabricate. ONE ScreeningResults block per investigation, never a second.',
    },

    StructuringChart: {
      props: z.object({
        title: z.string().optional(),
        transactions: z.array(
          z.object({
            date: z.string(),
            amount: z.number(),
            counterparty: z.string().optional(),
            reference: z.string().optional(),
          }),
        ),
        threshold: z.number(),
        thresholdLabel: z.string().optional(),
        currency: z.string().optional(),
      }),
      description:
        'Scatter plot for structuring/threshold-avoidance patterns. Shows clustering below a reporting threshold. More impactful than describing the pattern in text. threshold = regulatory cutoff in USD (e.g. 50000). CRITICAL: transactions MUST be embedded directly in props — NEVER use /state/ paths.',
    },

    InvestigationSummary: {
      props: z.object({
        name: z.string(),
        alert: z
          .object({
            alertId: z.string(),
            priority,
            date: z.string(),
            rules: z.array(z.string()).optional(),
          })
          .optional(),
      }),
      description:
        'Investigation header — shows rule triggers, color-coded by priority (priority drives the color only; the word "priority" is never displayed). Use as the FIRST (root) element. Entity details go in a KVList child, not here. Pre-populate children with ALL element IDs. Set the alert ONLY when priority is critical or high — omit it for medium/low cases. Always include rules — the alert renders nothing without them. Keep rules short (e.g. "Structuring detection", "Pass-through velocity") — no ALL CAPS, no long descriptions.',
    },

    ScoreGauge: {
      props: z.object({
        score: z.number(),
        label: z.string().optional(),
        size: z.number().optional(),
      }),
      description:
        'Semi-circle gauge (0-100). ONLY use for real scores from calibrated systems (e.g. vendor API). Never invent a score — use badge-based risk levels for qualitative assessment.',
    },

    BarChart: {
      props: z.object({
        data: z.array(
          z.object({
            label: z.string(),
            values: z.record(z.string(), z.number()),
          }),
        ),
        series: z.array(
          z.object({
            key: z.string(),
            label: z.string(),
            color: z.string().optional(),
            type: z.enum(['bar', 'line']).optional(),
          }),
        ),
        currency: z.string().optional(),
      }),
      description:
        'Interactive grouped bar chart with optional line overlay. Use for monthly/periodic volume trends — e.g. credits vs debits vs international wires over time. Each data point has a label (e.g. "Jan 2026") and a values object keyed by series key. Series defines what to plot: key matches the values key, label is displayed, type defaults to "bar" (set to "line" for trend overlay). Max 3-4 series for readability. Currency defaults to USD. CRITICAL: data MUST be embedded directly in props — NEVER use /state/ paths. Example: {"type":"BarChart","props":{"title":"Monthly Volume","data":[{"label":"Jan 2026","values":{"credits":621000,"debits":589200,"intl":412000}}],"series":[{"key":"credits","label":"Credits"},{"key":"debits","label":"Debits"},{"key":"intl","label":"Intl. wires","type":"line"}]},"children":[]}',
    },

    HorizontalBarChart: {
      props: z.object({
        items: z.array(
          z.object({
            label: z.string(),
            value: z.number(),
            color: z.string().optional(),
          }),
        ),
        currency: z.string().optional(),
        sorted: z.boolean().optional(),
      }),
      description:
        'Horizontal bar chart sorted by value (descending by default). Use for ranked comparisons — geographic exposure by jurisdiction, counterparty volume, flag/risk type concentration. Each item has a label and numeric value. Set sorted=false to preserve input order. Currency defaults to USD. CRITICAL: items MUST be embedded directly in props — NEVER use /state/ paths. Example: {"type":"HorizontalBarChart","props":{"title":"Geographic Exposure","items":[{"label":"Cyprus","value":478350},{"label":"Turkey","value":349130}]},"children":[]}',
    },

    FindingsCard: {
      props: z.object({
        title: z.string().optional(),
        findings: z.array(
          z.object({
            id: z.string(),
            severity: z.enum(['critical', 'high', 'medium', 'low']),
            title: z.string(),
            description: z.string(),
            evidence: z
              .array(
                z.union([
                  z.string(),
                  z.object({
                    description: z.string(),
                    url: z.string().optional(),
                    url_label: z.string().optional(),
                  }),
                ]),
              )
              .optional(),
          }),
        ),
        initialVisible: z.number().optional(),
      }),
      description:
        'Findings ordered by severity (critical → low). Keep titles short (e.g. "Sub-threshold structuring"). Descriptions: 1-2 sentences with specific evidence, no padding. id = short slug (e.g. "f1"). Collapses beyond 3 by default. Evidence: each item is { description, url?, url_label? } — description is the claim/fact; url_label is the human-readable source name (e.g. "Baselayer", "OFAC SDN list"); url is a clickable link, ONLY include when you have a real verified URL — NEVER invent or guess URLs. Do not include methodology metadata like "Source tier: Tier 1" or "Categories checked"; those are noise.',
    },
  },
  actions: {},
});
